import React from "react";
import Cell from "../Cell";
import ModulePropertyDialog from "../ModulePropertyDialog";
import { EDGE_THICKNESS_NAMES } from "../../../constants";
import { isMobileOnly } from "react-device-detect";
import { CirclePoint } from "../../style";

const BuildCell = ({
  type,
  rowId,
  tempId,
  sectionId,
  isFiller = false,
  errorMessage,
  tableManager,
  propertyList = [],
  onContextMenu,
  viewMode,
  reviewColumnColorMap,
  onValueChange,
  dialogProps,
}) => {
  const getCellBackgroundColor = (column, value) => {
    if (!reviewColumnColorMap) return null;

    // Always use column-specific key to match the color map format
    const normalizedValue = value || "";
    const mapKey = `${column}__${normalizedValue}`;

    return reviewColumnColorMap[mapKey] || null;
  };

  const getEdgeThickness = () => {
    return (
      <>
        {["front_edge", "partition_front_edge"].map((e) => {
          const property = propertyList.find(
            (prop) => prop.identifier === `${e}_thickness`
          );
          const column = `review_${e}_thickness`;

          const selectedOption =
            property && property.options.find((opt) => opt.selected);

          const value =
            property && type === "module"
              ? EDGE_THICKNESS_NAMES[selectedOption.identifier]
              : "";

          onValueChange(rowId, column, (value || "").trim());

          const backgroundColor = getCellBackgroundColor(column, value);

          return (
            <Cell
              key={column}
              rowId={rowId}
              groupId={sectionId}
              type={type}
              column={column}
              className="flex-grow-0 flex-shrink-0"
              customStyles={{
                width: isMobileOnly ? "15%" : "90px",
                backgroundColor: backgroundColor || undefined,
              }}
              tableManager={tableManager}
            >
              <div className="fs-10px">{value}</div>
            </Cell>
          );
        })}
      </>
    );
  };

  const getFrontOffset = () => {
    const property = propertyList.find(
      (prop) => prop.identifier === "front_offset"
    );
    const column = "review_front_offset";

    const selectedOption =
      property && property.options.find((opt) => opt.selected);

    let value =
      property && type === "module" && selectedOption.identifier !== "30"
        ? `${selectedOption.name} front offset`
        : "";

    onValueChange(rowId, column, (value || "").trim());

    const backgroundColor = getCellBackgroundColor(column, value);

    return (
      <Cell
        rowId={rowId}
        groupId={sectionId}
        type={type}
        column={column}
        className="flex-grow-0 flex-shrink-0"
        customStyles={{
          width: isMobileOnly ? "15%" : "80px",
          backgroundColor: backgroundColor || undefined,
        }}
        tableManager={tableManager}
      >
        <div className="fs-10px">{value}</div>
      </Cell>
    );
  };

  const getBacksheetTechnique = () => {
    const property = propertyList.find(
      (prop) => prop.identifier === "backsheet_build_technique"
    );
    const column = "review_backsheet_build_technique";

    const selectedOption =
      property && property.options.find((opt) => opt.selected);

    const value =
      property &&
      type === "module" &&
      ["grooved_backsheet", "screwed_backsheet"].includes(
        selectedOption.identifier
      )
        ? selectedOption.name
        : "";

    onValueChange(rowId, column, (value || "").trim());

    const backgroundColor = getCellBackgroundColor(column, value);

    return (
      <Cell
        rowId={rowId}
        groupId={sectionId}
        type={type}
        column={column}
        className="flex-grow-0 flex-shrink-0"
        customStyles={{
          width: isMobileOnly ? "15%" : "80px",
          backgroundColor: backgroundColor || undefined,
        }}
        tableManager={tableManager}
      >
        <div className="fs-10px">{value}</div>
      </Cell>
    );
  };

  const getMinifix = () => {
    const property = propertyList.find((prop) => prop.identifier === "minifix");

    const selectedOption =
      property && property.options.find((opt) => opt.selected);

    const value =
      property && type === "module"
        ? selectedOption.identifier === "yes"
          ? "Minifix"
          : ""
        : "";

    onValueChange(rowId, "review_minifix", (value || "").trim());

    const backgroundColor = getCellBackgroundColor("review_minifix", value);

    return (
      <Cell
        rowId={rowId}
        groupId={sectionId}
        type={type}
        column="review_minifix"
        className="flex-grow-0 flex-shrink-0"
        customStyles={{
          width: isMobileOnly ? "15%" : "60px",
          backgroundColor: backgroundColor || undefined,
        }}
        tableManager={tableManager}
      >
        <div className="fs-10px">{value}</div>
      </Cell>
    );
  };

  const propertiesToShow = [
    "front_edge_thickness",
    "minifix",
    "frame_depth",
    "partition_front_edge_thickness",
    "build_type",
    "frame_carcass_type",
    "open_carcass_type",
    "backsheet_build_technique",
    "open_carcass_backsheet_type",
  ];

  const defaultValues = {
    partition_front_edge_thickness: "2",
    frame_depth: "100",
    minifix: "no",
  };

  const renderProperties = () => {
    const names = [];

    for (const pro of propertyList) {
      if (!propertiesToShow.includes(pro.identifier) || pro.is_automated)
        continue;

      const selected = pro.options.find((opt) => opt.selected);

      if (
        !selected ||
        selected.identifier === defaultValues[pro.identifier] ||
        (pro.level === "project" && !pro.is_overridden_v2)
      )
        continue;

      names.push(selected.name);
    }

    return names.map((name, index) => (
      <div key={index} className="d-flex align-items-center gap-1">
        {index !== 0 && <CirclePoint />}
        <div>{name}</div>
      </div>
    ));
  };

  const properties = propertyList.filter((p) =>
    ["project", "admin"].includes(p.level)
  );

  return viewMode === "review" ? (
    <>
      {getEdgeThickness()}
      {getFrontOffset()}
      {getBacksheetTechnique()}
      {getMinifix()}
    </>
  ) : (
    <Cell
      rowId={rowId}
      groupId={sectionId}
      rowType={type}
      column="build"
      className="flex-grow-0 flex-shrink-0"
      customStyles={{
        width: isMobileOnly ? "15%" : "130px",
      }}
      tableManager={tableManager}
    >
      {type === "module" && (
        <>
          <div className="d-flex align-items-center flex-wrap gap-2">
            {renderProperties()}
          </div>
          {tableManager.isActive(type, rowId, "build") && (
            <ModulePropertyDialog
              {...dialogProps}
              properties={properties}
              mode="build"
            />
          )}
        </>
      )}
    </Cell>
  );
};

export default BuildCell;
